import React from 'react';
import { styled } from '@linaria/react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, COLORS, Icon, IconButton, typographyStyles } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import useOrgId from 'hooks/useOrgId.js';
import useAiProductTaggingPromo, { PROMO_VARIANT } from 'hooks/useAiProductTaggingPromo.js';
import { buildRoute, ORG_PRODUCT_FEED_NEW } from 'constants/routes.js';
import { ON_DEMAND_PRODUCT } from 'constants/products.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import {
  vod_ai_product_tagging_promo_connect_feed,
  vod_ai_product_tagging_promo_dismiss,
} from 'services/tracking/dataClickIdEventNames.js';

const { getBodyText } = typographyStyles;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 16px;
  border-radius: 12px;
  background-color: ${COLORS.light_000};
  color: ${COLORS.dark_700};
`;

const Sparkle = styled.span`
  display: flex;
  color: ${COLORS.dark_700};
`;

const Text = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 12px;
  flex: 1;
  min-width: 0;
`;

const Title = styled.span`
  ${getBodyText(12, 500)};
`;

const Description = styled.span`
  ${getBodyText(10, 400)};
  color: ${COLORS.dark_300};
`;

/**
 * The Library's line about AI product tagging: connect a feed first, then add
 * videos. Nothing while the first feed is being processed; the Home card tells
 * that part.
 */
const AiProductTaggingLibraryPromo = () => {
  const { t } = useLocalization();
  const history = useHistory();
  const orgId = useOrgId();
  const { selectedProduct = ON_DEMAND_PRODUCT } = useParams();
  const { variant, dismiss } = useAiProductTaggingPromo();

  if (variant !== PROMO_VARIANT.CONNECT_FEED && variant !== PROMO_VARIANT.TRY) return null;

  const connectFeed = () => {
    trackingService.trackClick({ clickId: vod_ai_product_tagging_promo_connect_feed });
    history.push(buildRoute(ORG_PRODUCT_FEED_NEW, { orgId, selectedProduct }));
  };

  return (
    <Bar data-test-id="ai-product-tagging-promo-library" data-variant={variant}>
      <Sparkle><Icon name="ai" size={20} /></Sparkle>
      {variant === PROMO_VARIANT.CONNECT_FEED ? (
        <Text>
          <Title>{t('videosVodPage.aiProductTaggingPromo.connectFeedTitle', {}, 'Let AI find the products in your videos')}</Title>
          <Description>
            {t('videosVodPage.aiProductTaggingPromo.connectFeedDescription', {}, 'Connect your product feed and AI matches the right products automatically. You approve what goes live.')}
          </Description>
        </Text>
      ) : (
        <Text>
          <Title>{t('videosVodPage.aiProductTaggingPromo.tryTitle', {}, 'Try AI Product Tagging by adding videos to your library')}</Title>
        </Text>
      )}
      {variant === PROMO_VARIANT.CONNECT_FEED && (
        <Button variant="solidOrange" size="small" isUppercase={false} onClick={connectFeed}>
          {t('videosVodPage.aiProductTaggingPromo.connectFeed', {}, 'Connect feed')}
        </Button>
      )}
      <IconButton
        iconName="cross"
        size="small"
        variant="transparent"
        aria-label={t('genericLabels.close', {}, 'Close')}
        onClick={() => { trackingService.trackClick({ clickId: vod_ai_product_tagging_promo_dismiss }); dismiss(); }}
      />
    </Bar>
  );
};

export default AiProductTaggingLibraryPromo;
